import { PointTooltipProps, ResponsiveLine } from "@nivo/line";
import React from "react";
import { graphPaletteV2, lightGreyText } from "../../../styles";
import { CompassParagraph } from "../../../components/common/styledComponents";
import { generatePaletteColors } from "../../../utils/graphUtils";
import { FormattedExposureData } from "../../../models/stewardshipModels";
import { formatPct } from "../../../utils/formattingUtils";
import { LegendCircle } from "../../../components/visuals/styledComponents";
import { FlexibleContainer } from "../../../components/containers/styledComponents";

const theme = {
  axis: {
    ticks: {
      text: {
        fill: lightGreyText,
        fontSize: 12,
        fontFamily: "Apercu",
      },
    },
  },
  grid: {
    line: {
      stroke: "#d3d3d3",
      strokeWidth: 0.4,
      strokeDasharray: "6 8",
    },
  },
};

export const getUniqueQuarterCount = (
  data: FormattedExposureData[]
): number => {
  const quarters = new Set<string>();

  data.forEach(({ data: portfolioData }) => {
    portfolioData.forEach(({ date }) => {
      quarters.add(date);
    });
  });

  return quarters.size;
};

const customTooltip = ({ point }: PointTooltipProps): JSX.Element => (
  <div
    style={{
      background: "white",
      padding: "9px 12px",
      border: "1px solid #ccc",
      borderRadius: "7px",
    }}
  >
    <FlexibleContainer>
      <CompassParagraph bold={true}>Fund:</CompassParagraph>
      <LegendCircle color={point.serieColor} />
      <CompassParagraph>{point.serieId}</CompassParagraph>
    </FlexibleContainer>
    <CompassParagraph>
      <strong>Exposure Weight:</strong> {formatPct(point.data.y as number)}
    </CompassParagraph>
  </div>
);

export const CompanyExposureChart: React.FC<{
  data: FormattedExposureData[];
}> = ({ data }) => {
  const colors = generatePaletteColors(data, graphPaletteV2);

  return data.length ? (
    <div style={{ height: "350px", width: "100%" }}>
      <ResponsiveLine
        data={data.map((serie) => ({
          ...serie,
          data: serie.data.map((point) => ({
            x: point.date,
            y: point.weight,
          })),
        }))}
        theme={theme}
        margin={{ top: 25, right: 50, bottom: 50, left: 70 }}
        xScale={{ type: "point" }}
        yScale={{
          type: "linear",
          min: "auto",
          max: "auto",
          stacked: false,
          reverse: false,
        }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 0,
          tickPadding: 5,
          tickRotation: 0,
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 5,
          tickRotation: 0,
          legendPosition: "middle",
          format: (value) => formatPct(value),
        }}
        colors={colors}
        lineWidth={2}
        pointSize={getUniqueQuarterCount(data) === 1 ? 6 : 0} // Set point size to 0 to hide the dots when multiple quarters
        pointBorderWidth={2}
        pointBorderColor={{ from: "serieColor" }}
        pointLabelYOffset={-12}
        useMesh={true}
        tooltip={customTooltip}
      />
    </div>
  ) : null;
};
